
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CodeEditor } from '../CodeEditor';
import { PreviewPane } from '../PreviewPane';

interface EditorTabsProps {
  htmlContent: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement> | string) => void;
  previewHtml: string;
  getPreviewHtmlWithExampleValues: (html: string) => string;
}

export function EditorTabs({
  htmlContent,
  onChange,
  previewHtml,
  getPreviewHtmlWithExampleValues
}: EditorTabsProps) {
  const [isChangeDetected, setIsChangeDetected] = useState(false);
  const changeDetectionTimerRef = React.useRef<number | null>(null);

  // Debug log when content changes
  useEffect(() => {
    console.log('EditorTabs: HTML content or preview HTML updated', {
      htmlContentLength: htmlContent?.length || 0,
      previewHtmlLength: previewHtml?.length || 0
    });
  }, [htmlContent, previewHtml]);
  
  // Clean up the timer on unmount
  useEffect(() => {
    return () => {
      if (changeDetectionTimerRef.current !== null) {
        clearTimeout(changeDetectionTimerRef.current);
      }
    };
  }, []);
  
  // Handle code changes with optimized debouncing
  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Mark as changing
    setIsChangeDetected(true);
    
    // Pass the change to parent component
    onChange(e);
    
    // Clear any existing timer
    if (changeDetectionTimerRef.current !== null) {
      clearTimeout(changeDetectionTimerRef.current);
    }
    
    // Reset change detection after a delay
    changeDetectionTimerRef.current = window.setTimeout(() => {
      setIsChangeDetected(false);
      changeDetectionTimerRef.current = null;
    }, 500);
  }, [onChange]);
  
  // Memoize components to prevent unnecessary re-renders
  const codeEditor = useMemo(() => (
    <CodeEditor 
      htmlContent={htmlContent} 
      onChange={handleCodeChange}
    />
  ), [htmlContent, handleCodeChange]);
  
  const previewPanel = useMemo(() => (
    <PreviewPane 
      previewHtml={previewHtml}
      getPreviewHtmlWithExampleValues={getPreviewHtmlWithExampleValues}
      isChangeDetected={isChangeDetected}
    />
  ), [previewHtml, getPreviewHtmlWithExampleValues, isChangeDetected]);

  return (
    <div 
      className="grid grid-cols-1 md:grid-cols-2"
      style={{ 
        contain: 'content',
        minHeight: '550px',
        height: '100%',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Left panel - Code Editor */}
      <div className="relative h-full border-r">
        {codeEditor}
      </div>
      
      {/* Right panel - Preview */}
      <div className="h-full">
        {previewPanel}
      </div>
    </div>
  );
}
